/**
 * Hilfsfunktionen zur Einordnung von Request-URLs des Export-Service.
 *
 * Wird vom ServiceErrorInterceptor genutzt, um zu entscheiden, ob ein Request
 * zur API gehört, aus welchem Bereich er kommt und unter welchem Schlüssel ein
 * Fehler dieses Endpunkts geführt wird.
 *
 * Bewusst als reine Funktionen ohne Angular-Abhängigkeit, damit sie ohne
 * TestBed geprüft werden können.
 */

/**
 * Bereich, aus dem ein API-Request stammt.
 *
 * - "admin": Admin-Center unter /admin/..., interne Nutzer
 * - "customer": Endkunden-/Wizard-Bereich
 */
export type ApiArea = 'admin' | 'customer';

/**
 * Pfad-Präfixe der bekannten API-Bereiche, jeweils bis unmittelbar vor das
 * Ressourcen-Segment.
 *
 * Die Präfixe sind bewusst explizit statt über eine ID-Heuristik ermittelt:
 * Routen-Parameter können beliebige Werte enthalten (auch "foo-a"), sodass sich
 * IDs nicht zuverlässig an ihrer Form erkennen lassen. Über die feste Position
 * bleibt die Menge möglicher Endpunkt-Schlüssel auf die Anzahl der Ressourcen
 * begrenzt.
 */
const RESOURCE_PREFIXES: ReadonlyArray<string> = [
  '/admin/v1/datev/incometax/',
  '/api/v1/export/datev/incometax/',
];

/**
 * Parst eine absolute URL. Relative oder syntaktisch ungültige URLs ergeben
 * undefined, statt eine Exception zu werfen.
 *
 * Wichtig für den Interceptor: Der Konfigurations-Abruf läuft über die
 * relative URL /assets/config/config.json durch die Interceptor-Kette. Ein
 * ungeschütztes new URL() würde dort den App-Start abbrechen.
 */
function parseAbsoluteUrl(url: string | undefined | null): URL | undefined {
  if (!url) {
    return undefined;
  }

  try {
    return new URL(url);
  } catch {
    return undefined;
  }
}

/**
 * Entfernt einen abschließenden Slash, damit "/basis" und "/basis/" gleich
 * behandelt werden.
 */
function withoutTrailingSlash(path: string): string {
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Liefert den Pfad eines Requests relativ zur API-Basis, oder undefined wenn
 * der Request nicht zur API gehört.
 *
 * Der Vergleich prüft den Origin exakt und den Basispfad nur an einer echten
 * Segmentgrenze. Eine reine Präfixprüfung würde sonst auch URLs wie
 * "<apiBaseUrl>.evil/..." oder "<apiBaseUrl>-v2/..." als Treffer zählen.
 */
export function apiPathOf(url: string, apiBaseUrl: string | undefined): string | undefined {
  const target = parseAbsoluteUrl(url);
  const base = parseAbsoluteUrl(apiBaseUrl);
  if (!target || !base) {
    return undefined;
  }

  if (target.origin !== base.origin) {
    return undefined;
  }

  const basePath = withoutTrailingSlash(base.pathname);
  const targetPath = target.pathname;

  // INFO: Basis ohne eigenen Pfad - jeder Pfad des Origins gehört zur API
  if (basePath === '') {
    return targetPath || '/';
  }

  if (targetPath === basePath) {
    return '/';
  }

  if (!targetPath.startsWith(`${basePath}/`)) {
    return undefined;
  }

  return targetPath.slice(basePath.length);
}

/**
 * Prüft, ob ein Request zur API-Basis des Export-Service gehört.
 */
export function matchesApiBase(url: string, apiBaseUrl: string | undefined): boolean {
  return apiPathOf(url, apiBaseUrl) !== undefined;
}

/**
 * Liefert den Bereich eines API-Requests, oder undefined wenn der Request
 * nicht zur API gehört.
 *
 * Die Unterscheidung erfolgt über das erste Pfadsegment, damit auch künftige
 * Versionspfade unter /admin/ korrekt eingeordnet werden.
 */
export function apiAreaOf(url: string, apiBaseUrl: string | undefined): ApiArea | undefined {
  const path = apiPathOf(url, apiBaseUrl);
  if (path === undefined) {
    return undefined;
  }

  return path === '/admin' || path.startsWith('/admin/') ? 'admin' : 'customer';
}

/**
 * Liefert einen stabilen Schlüssel für den Endpunkt eines API-Requests, oder
 * undefined wenn der Request nicht zur API gehört.
 *
 * Der Schlüssel endet mit dem Ressourcen-Segment; Path-Parameter und weitere
 * Unterpfade entfallen. Damit gilt:
 *
 * - /admin/v1/datev/incometax/export-job/<id>/audit/<rev>
 *   -> /admin/v1/datev/incometax/export-job
 *
 * Das begrenzt die Menge der Schlüssel auf die Anzahl der Ressourcen und ist
 * auch fachlich richtig: Ein 503 auf zwei verschiedene IDs derselben Ressource
 * ist derselbe Zustand.
 */
export function endpointKey(url: string, apiBaseUrl: string | undefined): string | undefined {
  const path = apiPathOf(url, apiBaseUrl);
  if (path === undefined) {
    return undefined;
  }

  for (const prefix of RESOURCE_PREFIXES) {
    if (path.startsWith(prefix)) {
      const resource = path.slice(prefix.length).split('/')[0];
      return resource ? `${prefix}${resource}` : prefix;
    }
  }

  // INFO: Unbekanntes Pfadmuster - auf die ersten zwei Segmente reduzieren,
  // damit die Schlüsselmenge auch dann begrenzt bleibt.
  const segments = path.split('/').filter(segment => segment.length > 0).slice(0, 2);
  return segments.length > 0 ? `/${segments.join('/')}` : '/';
}
